import type { IListicleMetrics, IListiclePaginated } from 'src/types/listicle';

import { useMemo } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';

import axios, { endpoints, fetcherGet } from 'src/lib/axios';

export function useListicles(page: number, pageSize: number, onlyWithRequests: boolean) {
  const key = `${endpoints.listicles.list}?page=${page}&pageSize=${pageSize}&onlyWithRequests=${onlyWithRequests}`;

  const { data, error, isLoading, mutate } = useSWR(key, fetcherGet, {
    revalidateOnFocus: false,
  });

  const memoized = useMemo(() => {
    const payload = data?.data as IListiclePaginated | undefined;
    return {
      searches: payload?.data ?? [],
      total: payload?.total ?? 0,
      isLoading,
      error,
      mutate,
    };
  }, [data?.data, error, isLoading, mutate]);

  return memoized;
}

// Nº de solicitudes de listicles pendientes de gestionar, para el badge del menú.
export function useListiclesPendingCount() {
  const { data, mutate } = useSWR(
    endpoints.listicles.pendingCount,
    async (url: string) => {
      const res = await axios.get(url);
      return (res.data?.count as number) ?? 0;
    },
    { revalidateOnFocus: false, refreshInterval: 60000 }
  );

  return { count: data ?? 0, refresh: mutate };
}

/**
 * Marca solicitudes como gestionadas (o las devuelve a pendientes) y refresca el badge del menú,
 * para que el número desaparezca en cuanto el equipo las gestione.
 */
export async function setListiclesHandled(resultIds: number[], handled: boolean) {
  await axios.post(endpoints.listicles.setHandled, { resultIds, handled });
  await globalMutate(endpoints.listicles.pendingCount);
}

// Métricas de conversión de las solicitudes (solicitadas, por estado y % conseguidas).
export function useListiclesMetrics() {
  const { data, mutate } = useSWR(
    endpoints.listicles.metrics,
    async (url: string) => {
      const res = await axios.get(url);
      return (res.data?.data as IListicleMetrics) ?? null;
    },
    { revalidateOnFocus: false }
  );
  return { metrics: data ?? null, refresh: mutate };
}

/**
 * Cambia el estado del pipeline (pending|in_progress|won|rejected) y la nota interna de una solicitud.
 * Refresca el badge del menú y las métricas.
 */
export async function setListicleRequestStatus(resultId: number, status: string, note?: string | null) {
  await axios.put(endpoints.listicles.setRequestStatus(resultId), { status, note: note ?? null });
  await globalMutate(endpoints.listicles.pendingCount);
  await globalMutate(endpoints.listicles.metrics);
}
